// Tracker #257 (Farmers, 10/9 and 10/12): the carrier and NAIC fields on ACORD 24/25/27/28
// are editable. The certificate still stores a carrier id per coverage row (NaicId) and the
// AMS prints the carrier name and NAIC from that record, so a typed carrier has to exist as a
// record before Save. Rules, per slot with a name:
//   - picked carrier, NAIC blank or equal to the record's  -> keep the picked id
//   - picked carrier, NAIC changed                         -> create a new carrier
//   - typed name with no id                                -> create a new carrier
// Existing records are never edited from a certificate: a wrong NAIC on a carrier other
// agencies also use would print on their certificates too. The AMS scopes the new record to
// the agency that created it. The same name + NAIC within one Save is created once.

export interface CarrierSlot {
  name: string;
  id: string | null;
  naic: string;
}

export interface CarrierSlotDeps {
  lookupById: (id: string) => Promise<{ id: string; name: string; naic: string } | null>;
  insert: (name: string, naic: string) => Promise<string>;
}

export async function ensureCarrierIds(slots: CarrierSlot[], deps: CarrierSlotDeps): Promise<(string | null)[]> {
  const created = new Map<string, string>();
  const out: (string | null)[] = [];
  for (const slot of slots) {
    const name = (slot.name ?? '').trim();
    const naic = (slot.naic ?? '').trim();
    if (!name) { out.push(null); continue; }

    if (slot.id) {
      const known = await deps.lookupById(slot.id);
      const sameRecord = !known || !naic || known.naic.trim() === naic;
      if (sameRecord) { out.push(slot.id); continue; }
    }

    const key = `${name.toLowerCase()}|${naic}`;
    let id = created.get(key);
    if (!id) {
      id = await deps.insert(name, naic);
      created.set(key, id);
    }
    out.push(id);
  }
  return out;
}

type InsurerLetters = 'A' | 'B' | 'C' | 'D' | 'E' | 'F';
export const INSURER_LETTERS: InsurerLetters[] = ['A', 'B', 'C', 'D', 'E', 'F'];

/** Shape shared by the ACORD 24 and 25 form data: insurerA..F, insurerAId..FId, insurerANaic..FNaic. */
export type InsurerSlotsData = Record<`insurer${InsurerLetters}`, string>
  & Record<`insurer${InsurerLetters}Id`, string | null>
  & Record<`insurer${InsurerLetters}Naic`, string>;

/** Resolves the six insurer slots of an ACORD 24/25 form in place. */
export async function applyInsurerSlots<T extends InsurerSlotsData>(d: T, deps: CarrierSlotDeps): Promise<T> {
  const slots = INSURER_LETTERS.map(l => ({ name: d[`insurer${l}`], id: d[`insurer${l}Id`], naic: d[`insurer${l}Naic`] }));
  const ids = await ensureCarrierIds(slots, deps);
  INSURER_LETTERS.forEach((l, i) => { (d as InsurerSlotsData)[`insurer${l}Id`] = ids[i]; });
  return d;
}
